"use client";

import { useEffect, useRef } from 'react';

const steps = [
  {
    id: 'step-1',
    number: '01',
    title: 'Diagnóstico',
    description: 'Analizamos tu situación actual, objetivos de negocio y requisitos técnicos. Sin compromiso, con total claridad.',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
      </svg>
    ),
    detail: 'Reunión inicial · Levantamiento de requerimientos · Propuesta técnica',
  },
  {
    id: 'step-2',
    number: '02',
    title: 'Diseño',
    description: 'Diseñamos la arquitectura técnica y el plan de ejecución, adaptando la solución a tu presupuesto y cronograma.',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
      </svg>
    ),
    detail: 'Arquitectura técnica · Prototipos · Validación con el cliente',
  },
  {
    id: 'step-3',
    number: '03',
    title: 'Desarrollo',
    description: 'Ejecutamos el proyecto con metodología ágil, con entregas parciales y comunicación continua en cada fase.',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
    detail: 'Sprints ágiles · Pruebas continuas · Control de calidad',
  },
  {
    id: 'step-4',
    number: '04',
    title: 'Entrega & Soporte',
    description: 'Implementamos la solución en producción y brindamos soporte técnico continuo para garantizar su correcto funcionamiento.',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    detail: 'Deploy · Capacitación · Mantenimiento preventivo',
  },
];

const caseStudies = [
  {
    id: 'case-1',
    metric: '40%',
    label: 'reducción de desperdicios',
    description: 'Sistema de automatización industrial implementado en planta de manufactura en Lara. Control de procesos con PLC Siemens y HMI personalizado.',
    tags: ['Automatización', 'PLC', 'SCADA'],
    accentColor: '#22C55E',
  },
  {
    id: 'case-2',
    metric: '24/7',
    label: 'monitoreo garantizado',
    description: 'Sistema CCTV de 32 cámaras IP con grabación en la nube, control de acceso biométrico y alertas en tiempo real para empresa comercial.',
    tags: ['CCTV', 'Control Acceso', 'Nube'],
    accentColor: '#EF4444',
  },
  {
    id: 'case-3',
    metric: '3x',
    label: 'velocidad de producción',
    description: 'Prototipado de piezas de repuesto con impresión 3D para línea de producción, eliminando tiempos de espera por importación de componentes.',
    tags: ['Impresión 3D', 'Manufactura', 'Repuestos'],
    accentColor: '#EC4899',
  },
];

export default function ProcessSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = sectionRef?.current?.querySelectorAll('.reveal-hidden');
    elements?.forEach((el) => observer?.observe(el));

    return () => observer?.disconnect();
  }, []);

  return (
    <section
      id="proceso"
      ref={sectionRef}
      className="relative py-24 md:py-32 px-6 md:px-12"
      style={{ background: 'linear-gradient(180deg, #0A0F1A 0%, #0D1525 100%)' }}
    >
      <div className="max-w-7xl mx-auto">
        {/* Section header */}
        <div className="mb-16 text-center reveal-hidden">
          <p className="section-label mb-4">// Metodología</p>
          <h2 className="font-heading font-800 text-4xl md:text-5xl text-white leading-tight tracking-tight mb-4">
            Cómo{' '}
            <span className="gradient-text-orange">trabajamos juntos</span>
          </h2>
          <p className="font-body text-[#8892A4] text-lg max-w-2xl mx-auto leading-relaxed">
            Un proceso claro y transparente, desde el primer contacto hasta la entrega y el soporte continuo.
          </p>
        </div>

        {/* Steps */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-24">
          {steps?.map((step, i) => (
            <div
              key={step?.id}
              className="relative reveal-hidden"
              style={{ transitionDelay: `${i * 0.1}s` }}
            >
              {/* Connector line (desktop) */}
              {i < steps?.length - 1 && (
                <div
                  className="absolute top-6 left-full w-full h-px -translate-y-0.5 hidden lg:block"
                  style={{
                    background: 'linear-gradient(90deg, rgba(7,205,225,0.3), rgba(7,205,225,0.05))',
                    zIndex: 0,
                  }}
                />
              )}

              <div
                className="relative z-10 glass-card rounded-xl p-6 h-full"
                style={{ border: '1px solid rgba(7,205,225,0.1)' }}
              >
                {/* Number */}
                <div className="flex items-center gap-3 mb-5">
                  <span
                    className="font-heading font-800 text-3xl leading-none"
                    style={{ color: 'rgba(7,205,225,0.2)' }}
                  >
                    {step?.number}
                  </span>
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center text-[#07CDE1]"
                    style={{ background: 'rgba(7,205,225,0.08)', border: '1px solid rgba(7,205,225,0.2)' }}
                  >
                    {step?.icon}
                  </div>
                </div>

                <h3 className="font-heading font-700 text-white text-lg mb-2">{step?.title}</h3>
                <p className="font-body text-[#8892A4] text-sm leading-relaxed mb-4">{step?.description}</p>
                <p className="text-[10px] font-body text-[#07CDE1] tracking-wider opacity-70">{step?.detail}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Case studies */}
        <div className="reveal-hidden">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
            <div>
              <p className="section-label mb-3">// Casos de éxito</p>
              <h2 className="font-heading font-800 text-3xl md:text-4xl text-white tracking-tight">
                Resultados que hablan
              </h2>
            </div>
            <p className="font-body text-[#8892A4] text-sm max-w-xs">
              Proyectos reales entregados a empresas venezolanas en diversas industrias.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {caseStudies?.map((cs, i) => (
              <div
                key={cs?.id}
                className="glass-card rounded-xl p-6 reveal-hidden"
                style={{
                  border: `1px solid ${cs?.accentColor}20`,
                  transitionDelay: `${0.3 + i * 0.1}s`,
                }}
              >
                {/* Metric */}
                <div
                  className="font-heading font-800 text-5xl mb-1"
                  style={{ color: cs?.accentColor }}
                >
                  {cs?.metric}
                </div>
                <p
                  className="font-body text-sm font-500 mb-4"
                  style={{ color: cs?.accentColor }}
                >
                  {cs?.label}
                </p>

                {/* Divider */}
                <div className="h-px mb-4" style={{ background: `linear-gradient(90deg, ${cs?.accentColor}40, transparent)` }} />

                <p className="font-body text-[#8892A4] text-sm leading-relaxed mb-4">{cs?.description}</p>

                <div className="flex flex-wrap gap-2">
                  {cs?.tags?.map((tag) => (
                    <span
                      key={`${cs?.id}-${tag}`}
                      className="text-[10px] font-body font-500 px-2 py-0.5 rounded-sm"
                      style={{
                        background: `${cs?.accentColor}10`,
                        border: `1px solid ${cs?.accentColor}25`,
                        color: cs?.accentColor,
                      }}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}